import React, { useRef } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { ROUTES } from 'constants/routes';
import { safeInternalPath } from 'utils/url';

/* ── Route guards ── */

export const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loading" />;
  return user && !user.isAnonymous ? children : <Navigate to={ROUTES.login} replace />;
};

// Guest-only pages (login, signup): someone who arrives here ALREADY signed in
// has no reason to see them — e.g. pressing "back" after logging in landed on
// the login form again (#115). Honor a safe ?redirect= deep link; otherwise
// route by role the same way Landing does.
//
// Redirects only when the user was signed in at the moment auth first settled
// on this page. Signing in / signing up *on* the page flips `user` mid-flight,
// and redirecting on that change would race the page's own post-auth
// navigation (owner briefly looks like a student while the profile loads, so
// the guard would win the race and dump them on /discover — or yank a fresh
// gym owner out of the signup onboarding flow).
export const GuestRoute = ({ children }) => {
  const { user, isGymOwner, isCoach, isAdmin, loading } = useAuth();
  const [searchParams] = useSearchParams();
  // null = auth hasn't settled yet; afterwards: was the visitor signed in?
  const arrivedSignedInRef = useRef(null);
  if (!loading && arrivedSignedInRef.current === null) {
    arrivedSignedInRef.current = !!(user && !user.isAnonymous);
  }
  if (loading) return <div className="page-loading" />;
  if (arrivedSignedInRef.current && user && !user.isAnonymous) {
    const redirect = safeInternalPath(searchParams.get('redirect'));
    const dest =
      redirect ||
      (isGymOwner || isCoach ? ROUTES.gymDashboard : isAdmin ? ROUTES.admin.hub : ROUTES.discover);
    return <Navigate to={dest} replace />;
  }
  return children;
};

export const GymOwnerRoute = ({ children }) => {
  const { user, canPublishEvents, isAdmin, loading } = useAuth();
  if (loading) return <div className="page-loading" />;
  if (!user || user.isAnonymous) return <Navigate to={ROUTES.login} replace />;
  // canPublishEvents, not isGymOwner/isCoach: admins must keep access to the
  // owner tools (regression fixed once before in 31cfb4d — an admin bounced
  // off /edit-event landed on Landing, which forwards admins to /admin, so
  // every Edit click read as "sends me straight back to admin").
  if (!canPublishEvents) return <Navigate to={ROUTES.home} replace />;
  // Money/bookings platform: gym owners must confirm their email before reaching
  // owner tools. Admins and already-verified accounts (e.g. Google) pass through.
  if (!user.emailVerified && !isAdmin) return <Navigate to={ROUTES.verifyEmail} replace />;
  return children;
};

// Guard for the event editor. Deliberately does NOT check canPublishEvents:
// that flag depends on the user profile doc, and when the profile read fails
// (or the role flag is momentarily missing) the GymOwnerRoute version of this
// guard silently bounced legitimate owners/admins off their own edit page on
// every click — while the event page still showed "You own this event".
// EditEvent itself renders an explicit no-access screen from the event's
// gymOwnerId, which is the authoritative check for this route.
export const EventEditorRoute = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return <div className="page-loading" />;
  if (!user || user.isAnonymous) return <Navigate to={ROUTES.login} replace />;
  if (!user.emailVerified && !isAdmin) return <Navigate to={ROUTES.verifyEmail} replace />;
  return children;
};

export const AdminRoute = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return <div className="page-loading" />;
  if (!user || user.isAnonymous) return <Navigate to={ROUTES.login} replace />;
  if (!isAdmin) return <Navigate to={ROUTES.home} replace />;
  return children;
};
